import { useEffect, useMemo, useState } from 'react';
// `xlsx-js-style` em vez de `xlsx`: é a MESMA API (fork do SheetJS), mas a
// versão community do `xlsx` descarta `cell.s` na escrita — o negrito do
// arquivo da OC viraria letra morta. Verificado no XML gerado: só este produz
// `<b/>` em xl/styles.xml. O `xlsx` puro segue no resto do app, onde ninguém
// estiliza célula.
import * as XLSX from 'xlsx-js-style';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import StandardTable from '../components/ui/StandardTable';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import { usePurchaseCart } from '../hooks/usePurchaseCart';
import { api } from '../services/api';
import CartFileUpload from '../components/purchasing/CartFileUpload';
import { GetBySku, IndexBySku } from '../utils/aggregation';
import { FormatDateTimeBr } from '../utils/date';
import { CountLabel, FormatInt, FormatPrice } from '../utils/format';
import { PURCHASING_STATUS_LABELS as STATUS_LABELS } from '../utils/status';

const DEFAULT_LIMIT = 15;

const SUPPLIER_NEXT_STATUS = {
    OPEN: 'PRODUCTION',
    PRODUCTION: 'INVOICED',
    INVOICED: 'SHIPPED',
};

// O BOTÃO fala o ato do fornecedor ("Pedido enviado"); o ESTADO que resulta
// dele fala do nosso lado ("Aguardando recebimento"). Pedido do dono: manter
// o verbo familiar no botão mesmo com o status renomeado.
const SUPPLIER_ACTION_LABELS = {
    SHIPPED: 'Pedido enviado',
};

const formatDate = (value) => FormatDateTimeBr(value, { blank: '-' });

const Purchasing = () => {
    const toast = useToast();
    const { user, checkPermission } = useAuth();
    const canCreatePurchaseOrders = checkPermission('purchasing:create');
    const canUpdatePurchaseOrders = checkPermission('purchasing:update');
    const {
        items: purchaseCart,
        summary: purchaseCartSummary,
        addItem: addCartItem,
        updateItemQuantity: updateCartQuantity,
        removeItem: removeCartItem,
        clear: clearPurchaseCart,
        payloadItems: purchaseCartPayloadItems,
    } = usePurchaseCart();

    const [rows, setRows] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
    const [statusFilter, setStatusFilter] = useState('');

    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [itemDrafts, setItemDrafts] = useState({});
    const [itemCostDrafts, setItemCostDrafts] = useState({});
    const [generatingPurchaseOrders, setGeneratingPurchaseOrders] = useState(false);
    const [catalogOptions, setCatalogOptions] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [approvalDraft, setApprovalDraft] = useState({
        sku: '',
        quantity_ordered: 1,
        unit_cost: 0,
    });
    const [cartDraft, setCartDraft] = useState({
        sku: '',
        item_id: '',
        title: '',
        bling_stock: 0,
        cost_price: 0,
        quantity: 1,
    });

    const offset = (page - 1) * pageSize;

    // Índice por SKU normalizado — mata os `.find` lineares repetidos (B-6).
    const catalogBySku = useMemo(() => IndexBySku(catalogOptions), [catalogOptions]);

    const fetchRows = async () => {
        setLoading(true);
        try {
            const response = await api.getPurchaseOrders({
                limit: pageSize,
                offset,
                ...(statusFilter ? { status: statusFilter } : {}),
                include_cancelled: Boolean(statusFilter),
            });

            const allRows = Array.isArray(response?.results) ? response.results : [];
            setRows(allRows);
            setPaging({
                total: Number(response?.paging?.total || 0),
                limit: Number(response?.paging?.limit || pageSize),
                offset: Number(response?.paging?.offset || offset),
            });
        } catch (error) {
            toast.error(error.message || 'Erro ao carregar OCs');
        } finally {
            setLoading(false);
        }
    };

    const fetchCatalogOptions = async () => {
        if (!canCreatePurchaseOrders) {
            setCatalogOptions([]);
            return;
        }

        setCatalogLoading(true);
        try {
            const limit = 200;
            let offsetCursor = 0;
            let total = Infinity;
            const items = [];

            while (offsetCursor < total && offsetCursor < 5000) {
                const response = await api.getCatalogProducts({
                    limit,
                    offset: offsetCursor,
                    sort_by: 'sku',
                    sort_direction: 'asc',
                });

                const pageRows = Array.isArray(response?.results) ? response.results : [];
                const pageTotal = Number(response?.paging?.total || pageRows.length || 0);
                total = Number.isFinite(pageTotal) ? pageTotal : pageRows.length;
                items.push(...pageRows);

                if (pageRows.length < limit) {
                    break;
                }

                offsetCursor += limit;
            }

            const bySku = new Map();
            for (const product of items) {
                const sku = String(product?.sku || '').trim();
                if (!sku || bySku.has(sku)) {
                    continue;
                }
                bySku.set(sku, {
                    sku,
                    title: product?.title || sku,
                    bling_stock: Number(product?.stock?.bling || 0),
                    cost_price: Number(product?.cost_price || 0),
                    // EAN, marca e código do fornecedor entram porque o upload
                    // resolve o arquivo (que traz só EAN+QTD) contra ESTE
                    // catálogo, e porque a OC é separada por marca.
                    ean: product?.ean || null,
                    brand: product?.brand || null,
                    supplier_code: product?.supplier_code || null,
                });
            }

            setCatalogOptions([...bySku.values()].sort((a, b) => a.sku.localeCompare(b.sku)));
        } catch (error) {
            toast.error(error?.message || 'Erro ao carregar catalogo de produtos');
        } finally {
            setCatalogLoading(false);
        }
    };

    const fetchDetails = async (id) => {
        if (!id) {
            return;
        }
        setDetailsLoading(true);
        try {
            const response = await api.getPurchaseOrder(id);
            setSelectedOrder(response || null);
            const draftValues = {};
            const costValues = {};
            (response?.items || []).forEach((item) => {
                draftValues[item.id] = Number(item.quantity_confirmed || 0);
                costValues[item.id] = Number(item.unit_cost || 0);
            });
            setItemDrafts(draftValues);
            setItemCostDrafts(costValues);
        } catch (error) {
            toast.error(error.message || 'Erro ao carregar detalhes da OC');
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, statusFilter]);

    useEffect(() => {
        fetchCatalogOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canCreatePurchaseOrders]);

    useEffect(() => {
        if (!selectedOrderId) {
            setSelectedOrder(null);
            return;
        }

        fetchDetails(selectedOrderId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrderId]);

    const currentUserType = String(user?.organization_type || '').toUpperCase();
    const currentOrderStatus = String(selectedOrder?.status || '').toUpperCase();
    const isRootUser = currentUserType === 'ROOT';
    const isSupplierUser = currentUserType === 'SUPPLIER';
    const isOrderRequester = String(selectedOrder?.created_by_user_id || '') === String(user?.id || '');

    const canSupplierEditItems = useMemo(
        () => canUpdatePurchaseOrders && isSupplierUser && currentOrderStatus === 'OPEN',
        [canUpdatePurchaseOrders, isSupplierUser, currentOrderStatus],
    );

    const canApprovalEditItems = useMemo(
        () => canUpdatePurchaseOrders && isRootUser && currentOrderStatus === 'APPROVAL',
        [canUpdatePurchaseOrders, isRootUser, currentOrderStatus],
    );

    const canCancelOrder = useMemo(() => {
        if (!selectedOrder?.id) {
            return false;
        }
        if (['RECEIVED', 'CANCELLED'].includes(currentOrderStatus)) {
            return false;
        }
        if (isRootUser && canUpdatePurchaseOrders) {
            return true;
        }
        if (isOrderRequester && (canCreatePurchaseOrders || canUpdatePurchaseOrders)) {
            return true;
        }
        return false;
    }, [
        selectedOrder?.id,
        currentOrderStatus,
        isRootUser,
        isOrderRequester,
        canCreatePurchaseOrders,
        canUpdatePurchaseOrders,
    ]);

    const nextStatus = useMemo(() => {
        if (!canUpdatePurchaseOrders) {
            return null;
        }

        if (isRootUser && currentOrderStatus === 'APPROVAL') {
            return 'OPEN';
        }

        if (isSupplierUser) {
            return SUPPLIER_NEXT_STATUS[currentOrderStatus] || null;
        }

        return null;
    }, [canUpdatePurchaseOrders, isRootUser, isSupplierUser, currentOrderStatus]);

    const advanceStatus = async () => {
        if (!selectedOrder?.id || !nextStatus) {
            return;
        }

        setSaving(true);
        try {
            const shouldSendSupplierQuantities =
                canSupplierEditItems && currentOrderStatus === 'OPEN' && nextStatus === 'PRODUCTION';
            const shouldSendApprovalItems =
                canApprovalEditItems && currentOrderStatus === 'APPROVAL' && nextStatus === 'OPEN';
            const payloadItems = shouldSendApprovalItems
                ? (selectedOrder.items || []).map((item) => ({
                      id: String(item.id || '').startsWith('new-') ? undefined : item.id,
                      sku: item.sku,
                      quantity_ordered: Number(itemDrafts[item.id] ?? item.quantity_ordered ?? 0),
                      quantity_confirmed: Number(itemDrafts[item.id] ?? item.quantity_ordered ?? 0),
                      unit_cost: Number(itemCostDrafts[item.id] ?? item.unit_cost ?? 0),
                  }))
                : shouldSendSupplierQuantities
                  ? (selectedOrder.items || []).map((item) => ({
                        id: item.id,
                        quantity_confirmed: Number(itemDrafts[item.id] ?? item.quantity_confirmed ?? 0),
                    }))
                  : undefined;

            const updated = await api.updatePurchaseOrderStatus(selectedOrder.id, nextStatus, {
                items: payloadItems,
            });
            setSelectedOrder(updated || null);
            toast.success(`Status atualizado para ${STATUS_LABELS[nextStatus] || nextStatus}`);
            setSelectedOrderId(null);
            setSelectedOrder(null);
            setItemDrafts({});
            setItemCostDrafts({});
            await fetchRows();
        } catch (error) {
            toast.error(error.message || 'Erro ao atualizar status da OC');
        } finally {
            setSaving(false);
        }
    };

    /**
     * "Aprovada" = já passou da mesa de aprovação. DRAFT e APPROVAL ainda estão
     * em edição; CANCELLED não tem pedido a mandar.
     */
    const isOrderApproved = useMemo(
        () => Boolean(selectedOrder?.id) && !['DRAFT', 'APPROVAL', 'CANCELLED'].includes(currentOrderStatus),
        [selectedOrder, currentOrderStatus],
    );

    const downloadPurchaseOrderXlsx = () => {
        if (!selectedOrder) {
            return;
        }

        try {
            // Cabeçalho sem linha "Campo/Valor": são duas colunas óbvias, e o
            // rótulo delas só ocupava a primeira linha do arquivo.
            const summaryRows = [
                ['Codigo da OC', selectedOrder.code || '-'],
                ['Fornecedor', selectedOrder.supplier_name || '-'],
                ['Status', STATUS_LABELS[selectedOrder.status] || selectedOrder.status || '-'],
                ['Atualizado em', formatDate(selectedOrder.updated_at)],
                ['Criado em', formatDate(selectedOrder.created_at)],
                ['Recebido em', formatDate(selectedOrder.received_at)],
            ];

            const itemsHeader = [
                'SKU',
                'Cod. fornecedor',
                'Descrição do produto',
                'EAN',
                'Quantidade',
                'Custo unitario',
                'Quantidade recebida',
            ];
            const itemRows = (selectedOrder.items || []).map((item) => [
                item.sku || '-',
                item.supplier_code || 'N/A',
                item.title || '-',
                item.ean || GetBySku(catalogBySku, item.sku)?.ean || '-',
                // UMA quantidade só. Se o aprovador mexeu, o número dele é que
                // vale — a solicitada vira histórico interno e não interessa a
                // quem recebe o pedido.
                Number(item.quantity_confirmed || item.quantity_ordered || 0),
                Number(item.unit_cost || 0),
                Number(item.quantity_received || 0),
            ]);

            const workbook = XLSX.utils.book_new();
            const sheetRows = [...summaryRows, [], itemsHeader, ...itemRows];
            const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

            // Negrito nos RÓTULOS do cabeçalho (coluna A do resumo) e em toda a
            // linha de cabeçalho da tabela — é o que separa rótulo de dado num
            // arquivo que vai ser lido fora do sistema.
            const linhaDoCabecalhoDaTabela = summaryRows.length + 1;
            for (let linha = 0; linha < summaryRows.length; linha += 1) {
                const celula = worksheet[XLSX.utils.encode_cell({ r: linha, c: 0 })];
                if (celula) celula.s = { font: { bold: true } };
            }
            for (let coluna = 0; coluna < itemsHeader.length; coluna += 1) {
                const celula = worksheet[XLSX.utils.encode_cell({ r: linhaDoCabecalhoDaTabela, c: coluna })];
                if (celula) celula.s = { font: { bold: true } };
            }

            // Larguras para o arquivo abrir legível, sem ninguém arrastar coluna.
            worksheet['!cols'] = [
                { wch: 22 },
                { wch: 18 },
                { wch: 42 },
                { wch: 16 },
                { wch: 12 },
                { wch: 14 },
                { wch: 18 },
            ];
            XLSX.utils.book_append_sheet(workbook, worksheet, 'OC');

            const safeCode = String(selectedOrder.code || selectedOrder.id || 'oc')
                .replace(/[^\w.-]+/g, '_')
                .slice(0, 80);
            XLSX.writeFile(workbook, `${safeCode}.xlsx`);
        } catch (error) {
            toast.error(error?.message || 'Erro ao gerar arquivo XLSX da OC');
        }
    };

    const cancelPurchaseOrder = async () => {
        if (!selectedOrder?.id || !canCancelOrder) {
            return;
        }

        const confirmation = window.prompt('Digite CANCELAR para confirmar o cancelamento da OC');
        if (confirmation !== 'CANCELAR') {
            return;
        }

        setSaving(true);
        try {
            const updated = await api.updatePurchaseOrderStatus(selectedOrder.id, 'CANCELLED');
            setSelectedOrder(updated || null);
            toast.success('OC cancelada com sucesso');
            setSelectedOrderId(null);
            setSelectedOrder(null);
            setItemDrafts({});
            setItemCostDrafts({});
            await fetchRows();
        } catch (error) {
            toast.error(error.message || 'Erro ao cancelar OC');
        } finally {
            setSaving(false);
        }
    };

    const addApprovalItem = () => {
        if (!canApprovalEditItems || !selectedOrder) {
            return;
        }

        const sku = String(approvalDraft.sku || '').trim();
        const quantityOrdered = Math.floor(Number(approvalDraft.quantity_ordered || 0));
        const unitCost = Number(approvalDraft.unit_cost || 0);
        if (!sku || !Number.isFinite(quantityOrdered) || quantityOrdered <= 0) {
            toast.error('Informe SKU e quantidade valida para adicionar item');
            return;
        }

        const catalogEntry = GetBySku(catalogBySku, sku) || null;
        const existingBySku = (selectedOrder.items || []).some(
            (item) =>
                String(item?.sku || '')
                    .trim()
                    .toUpperCase() === sku.toUpperCase(),
        );
        if (existingBySku) {
            toast.error('Este SKU ja existe na OC');
            return;
        }

        const temporaryId = `new-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
        const nextItem = {
            id: temporaryId,
            purchase_order_id: selectedOrder.id,
            organization_id: selectedOrder.organization_id,
            sku,
            quantity_ordered: quantityOrdered,
            quantity_confirmed: quantityOrdered,
            quantity_received: 0,
            title: catalogEntry?.title || sku,
            supplier_code: null,
            unit_cost: Number.isFinite(unitCost) ? unitCost : 0,
        };

        setSelectedOrder((current) =>
            current
                ? {
                      ...current,
                      items: [...(current.items || []), nextItem],
                  }
                : current,
        );
        setItemDrafts((current) => ({
            ...current,
            [temporaryId]: quantityOrdered,
        }));
        setItemCostDrafts((current) => ({
            ...current,
            [temporaryId]: Number.isFinite(unitCost) ? unitCost : 0,
        }));
        setApprovalDraft({
            sku: '',
            quantity_ordered: 1,
            unit_cost: 0,
        });
    };

    const removeApprovalItem = (itemId) => {
        if (!canApprovalEditItems || !selectedOrder) {
            return;
        }

        setSelectedOrder((current) =>
            current
                ? {
                      ...current,
                      items: (current.items || []).filter((item) => String(item.id) !== String(itemId)),
                  }
                : current,
        );
        setItemDrafts((current) => {
            const next = { ...current };
            delete next[itemId];
            return next;
        });
        setItemCostDrafts((current) => {
            const next = { ...current };
            delete next[itemId];
            return next;
        });
    };

    const addDraftItemToCart = () => {
        const quantity = Math.floor(Number(cartDraft.quantity || 0));
        const selectedSkuMeta = GetBySku(catalogBySku, cartDraft.sku) || null;
        const payload = {
            sku: cartDraft.sku,
            item_id: cartDraft.item_id,
            title: cartDraft.title || selectedSkuMeta?.title || 'Produto',
            product_name: cartDraft.title || selectedSkuMeta?.title || 'Produto',
            bling_stock: Number(selectedSkuMeta?.bling_stock ?? cartDraft.bling_stock ?? 0),
            cost_price: Number(selectedSkuMeta?.cost_price ?? cartDraft.cost_price ?? 0),
            quantity,
        };

        const added = addCartItem(payload);
        if (!added) {
            toast.error('Informe SKU ou MLB e uma quantidade valida');
            return;
        }

        toast.success('Produto adicionado ao carrinho');
        setCartDraft({
            sku: '',
            item_id: '',
            title: '',
            bling_stock: 0,
            cost_price: 0,
            quantity: 1,
        });
    };

    const generatePurchaseOrdersFromCart = async () => {
        if (!canCreatePurchaseOrders) {
            return;
        }

        if (purchaseCart.length === 0) {
            toast.error('Adicione ao menos um produto no carrinho');
            return;
        }

        setGeneratingPurchaseOrders(true);
        try {
            const response = await api.generatePurchaseOrders({
                items: purchaseCartPayloadItems,
            });
            const createdCount = Number(response?.total || response?.results?.length || 0);
            toast.success(`${createdCount} OC(s) gerada(s) com sucesso`);
            clearPurchaseCart();
            await fetchRows();
        } catch (error) {
            toast.error(error.message || 'Erro ao gerar ordens de compra');
        } finally {
            setGeneratingPurchaseOrders(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: 'OC',
                cellClassName: 'font-mono font-semibold text-black dark:text-white',
            },
            {
                key: 'supplier_name',
                label: 'Fornecedor',
                getValue: (row) => row.supplier_name || '-',
            },
            {
                key: 'status',
                label: 'Status',
                render: (row) => STATUS_LABELS[row.status] || row.status || '-',
            },
            {
                key: 'created_at',
                label: 'Criado em',
                render: (row) => formatDate(row.created_at),
            },
            {
                key: 'updated_at',
                label: 'Atualizado em',
                render: (row) => formatDate(row.updated_at),
            },
        ],
        [],
    );

    return (
        <div className="space-y-6">
            <PageHeader title="Ordens de compra" subtitle="Acompanhe e atualize o fluxo das OCs por fornecedor" />

            {canCreatePurchaseOrders ? (
                <div className="space-y-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h3 className="text-sm font-semibold text-black dark:text-white">Carrinho de compra</h3>
                            <p className="text-xs text-mono-600 dark:text-mono-400">
                                {CountLabel(purchaseCartSummary.totalItems, 'SKU')} |{' '}
                                {CountLabel(purchaseCartSummary.totalUnits, 'unidade')} | Custo estimado:{' '}
                                {FormatPrice(purchaseCartSummary.estimatedCost)}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Porta de entrada por arquivo — só existe AQUI, na
                                Ordem de Compra, embora o carrinho seja o mesmo
                                de /operacoes e do sellout. */}
                            <CartFileUpload
                                catalogo={catalogOptions}
                                onAdicionar={addCartItem}
                                desabilitado={catalogLoading}
                            />
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={clearPurchaseCart}
                                disabled={purchaseCart.length === 0 || generatingPurchaseOrders}
                            >
                                Limpar
                            </Button>
                            <Button
                                size="sm"
                                onClick={generatePurchaseOrdersFromCart}
                                disabled={purchaseCart.length === 0 || generatingPurchaseOrders}
                            >
                                {generatingPurchaseOrders ? 'Gerando...' : 'Gerar OC'}
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                        <Select
                            label="SKU"
                            value={cartDraft.sku}
                            onChange={(event) => {
                                const nextSku = String(event.target.value || '').trim();
                                const selectedSkuMeta = GetBySku(catalogBySku, nextSku) || null;
                                setCartDraft((current) => ({
                                    ...current,
                                    sku: nextSku,
                                    title: selectedSkuMeta?.title || current.title || '',
                                    bling_stock: Number(selectedSkuMeta?.bling_stock || 0),
                                    cost_price: Number(selectedSkuMeta?.cost_price || 0),
                                }));
                            }}
                            disabled={catalogLoading}
                        >
                            <option value="">Selecione um SKU</option>
                            {catalogOptions.map((entry) => (
                                <option key={entry.sku} value={entry.sku}>
                                    {entry.sku}
                                </option>
                            ))}
                        </Select>
                        <Input
                            label="MLB (opcional)"
                            value={cartDraft.item_id}
                            onChange={(event) =>
                                setCartDraft((current) => ({ ...current, item_id: event.target.value }))
                            }
                            placeholder="Ex.: MLB123456"
                        />
                        <Input
                            label="Nome (opcional)"
                            value={cartDraft.title}
                            onChange={(event) => setCartDraft((current) => ({ ...current, title: event.target.value }))}
                            placeholder="Nome do produto"
                        />
                        <Input
                            label="Quantidade"
                            type="number"
                            min={1}
                            value={cartDraft.quantity}
                            onChange={(event) =>
                                setCartDraft((current) => ({
                                    ...current,
                                    quantity: event.target.value,
                                }))
                            }
                        />
                    </div>

                    <div className="flex justify-end">
                        <Button size="sm" variant="secondary" onClick={addDraftItemToCart}>
                            Adicionar ao carrinho
                        </Button>
                    </div>

                    {purchaseCart.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="border-b border-mono-200 dark:border-mono-800">
                                    <tr>
                                        <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            SKU
                                        </th>
                                        <th className="w-full max-w-0 px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Descrição
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            MLB
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Estoque (Bling)
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Preco custo
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Quantidade
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Acoes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                                    {purchaseCart.map((item) => (
                                        <tr key={item.key}>
                                            <td className="px-3 py-2 text-xs font-mono text-mono-700 dark:text-mono-300">
                                                {item.sku || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-black dark:text-white">
                                                {item.product_name || item.title || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-xs font-mono text-mono-700 dark:text-mono-300">
                                                {Array.isArray(item.item_ids) && item.item_ids.length > 0
                                                    ? item.item_ids.join(', ')
                                                    : item.item_id || '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-black dark:text-white">
                                                {FormatInt(item.bling_stock)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-black dark:text-white">
                                                {FormatPrice(item.cost_price)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={item.quantity}
                                                    onChange={(event) =>
                                                        updateCartQuantity(item.key, event.target.value)
                                                    }
                                                    className="w-24 rounded-lg border border-mono-300 bg-white px-2 py-1 text-right text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                                                />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right">
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => removeCartItem(item.key)}
                                                >
                                                    Remover
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm text-mono-500 dark:text-mono-500">
                            Adicione itens para gerar OC diretamente por esta pagina.
                        </p>
                    )}
                </div>
            ) : null}

            <StandardTable
                rows={loading ? [] : rows}
                columns={columns}
                rowKey={(row) => row.id}
                emptyMessage={loading ? 'Carregando OCs...' : 'Nenhuma OC encontrada.'}
                countLabel="OCs"
                searchEnabled={false}
                toolbarRight={
                    <Select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                        className="w-56"
                    >
                        <option value="">Todos os status</option>
                        <option value="APPROVAL">Aprovacao</option>
                        <option value="OPEN">Aguardando fornecedor</option>
                        <option value="PRODUCTION">Pedido feito</option>
                        <option value="INVOICED">NF emitida</option>
                        <option value="SHIPPED">Aguardando recebimento</option>
                        <option value="RECEIVED">Pedido recebido</option>
                        <option value="CANCELLED">Cancelado</option>
                    </Select>
                }
                sortMode="server"
                sortState={{ columnKey: null, direction: 'none' }}
                paginationMode="server"
                totalItems={paging.total}
                currentPage={page}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={(nextPageSize) => {
                    setPageSize(nextPageSize);
                    setPage(1);
                }}
                pageSizeOptions={[15, 30, 50, 100, 200]}
                onRowClick={(row) => setSelectedOrderId(row.id)}
                exportEnabled
                exportFileName="purchase-orders"
            />

            <Modal
                isOpen={Boolean(selectedOrderId)}
                onClose={() => {
                    setSelectedOrderId(null);
                    setSelectedOrder(null);
                    setItemDrafts({});
                    setItemCostDrafts({});
                    setApprovalDraft({
                        sku: '',
                        quantity_ordered: 1,
                        unit_cost: 0,
                    });
                }}
                title={selectedOrder?.code ? `OC ${selectedOrder.code}` : 'Detalhes da OC'}
                size="6xl"
            >
                {detailsLoading ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">Carregando detalhes...</p>
                ) : selectedOrder ? (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div>
                                <p className="text-xs uppercase text-mono-500 dark:text-mono-400">Fornecedor</p>
                                <p className="text-sm font-semibold text-black dark:text-white">
                                    {selectedOrder.supplier_name || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase text-mono-500 dark:text-mono-400">Status</p>
                                <p className="text-sm font-semibold text-black dark:text-white">
                                    {STATUS_LABELS[selectedOrder.status] || selectedOrder.status}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase text-mono-500 dark:text-mono-400">Atualizado em</p>
                                <p className="text-sm font-semibold text-black dark:text-white">
                                    {formatDate(selectedOrder.updated_at)}
                                </p>
                            </div>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-mono-200 dark:border-mono-800">
                            {canApprovalEditItems ? (
                                <div className="grid grid-cols-1 gap-2 border-b border-mono-200 bg-mono-50 px-3 py-3 dark:border-mono-800 dark:bg-mono-950 md:grid-cols-4">
                                    <Select
                                        value={approvalDraft.sku}
                                        onChange={(event) => {
                                            const sku = String(event.target.value || '').trim();
                                            const catalogEntry = GetBySku(catalogBySku, sku) || null;
                                            setApprovalDraft((current) => ({
                                                ...current,
                                                sku,
                                                unit_cost: Number(catalogEntry?.cost_price || current.unit_cost || 0),
                                            }));
                                        }}
                                    >
                                        <option value="">Adicionar SKU</option>
                                        {catalogOptions.map((entry) => (
                                            <option key={entry.sku} value={entry.sku}>
                                                {entry.sku}
                                            </option>
                                        ))}
                                    </Select>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={approvalDraft.quantity_ordered}
                                        onChange={(event) =>
                                            setApprovalDraft((current) => ({
                                                ...current,
                                                quantity_ordered: event.target.value,
                                            }))
                                        }
                                        placeholder="Quantidade"
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        value={approvalDraft.unit_cost}
                                        onChange={(event) =>
                                            setApprovalDraft((current) => ({
                                                ...current,
                                                unit_cost: event.target.value,
                                            }))
                                        }
                                        placeholder="Custo unitario"
                                    />
                                    <Button size="sm" variant="secondary" onClick={addApprovalItem}>
                                        Incluir item
                                    </Button>
                                </div>
                            ) : null}
                            <table className="w-full">
                                <thead className="border-b border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-950">
                                    <tr>
                                        <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            SKU
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Cod. fornecedor
                                        </th>
                                        <th className="w-full max-w-0 px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Descrição
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Solicitado
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Confirmado
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Custo unit.
                                        </th>
                                        <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                            Recebido
                                        </th>
                                        {canApprovalEditItems ? (
                                            <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                Acoes
                                            </th>
                                        ) : null}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                                    {(selectedOrder.items || []).map((item) => (
                                        <tr key={item.id}>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-mono-700 dark:text-mono-300">
                                                {item.sku}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-mono-700 dark:text-mono-300">
                                                {item.supplier_code || 'N/A'}
                                            </td>
                                            {/* Só a descrição encolhe (`w-full
                                                max-w-0` + truncate); as demais
                                                colunas são dados de conferência
                                                e ficam inteiras. A marca saiu:
                                                já está no cabeçalho da OC. */}
                                            <td
                                                className="w-full max-w-0 truncate px-3 py-2 text-sm text-black dark:text-white"
                                                title={item.title || ''}
                                            >
                                                {item.title || '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-black dark:text-white">
                                                {canApprovalEditItems ? (
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        value={itemDrafts[item.id] ?? item.quantity_ordered ?? 0}
                                                        onChange={(event) =>
                                                            setItemDrafts((current) => ({
                                                                ...current,
                                                                [item.id]: event.target.value,
                                                            }))
                                                        }
                                                        className="w-24 rounded-lg border border-mono-300 bg-white px-2 py-1 text-right text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                                                    />
                                                ) : (
                                                    item.quantity_ordered
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right">
                                                {canSupplierEditItems ? (
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={item.quantity_ordered}
                                                        value={itemDrafts[item.id] ?? item.quantity_confirmed ?? 0}
                                                        onChange={(event) =>
                                                            setItemDrafts((current) => ({
                                                                ...current,
                                                                [item.id]: event.target.value,
                                                            }))
                                                        }
                                                        className="w-24 rounded-lg border border-mono-300 bg-white px-2 py-1 text-right text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                                                    />
                                                ) : (
                                                    <span className="text-sm text-black dark:text-white">
                                                        {canApprovalEditItems
                                                            ? Number(itemDrafts[item.id] ?? item.quantity_ordered ?? 0)
                                                            : item.quantity_confirmed}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right">
                                                {canApprovalEditItems ? (
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step="0.01"
                                                        value={itemCostDrafts[item.id] ?? item.unit_cost ?? 0}
                                                        onChange={(event) =>
                                                            setItemCostDrafts((current) => ({
                                                                ...current,
                                                                [item.id]: event.target.value,
                                                            }))
                                                        }
                                                        className="w-28 rounded-lg border border-mono-300 bg-white px-2 py-1 text-right text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                                                    />
                                                ) : (
                                                    <span className="text-sm text-black dark:text-white">
                                                        {FormatPrice(item.unit_cost)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-black dark:text-white">
                                                {item.quantity_received}
                                            </td>
                                            {canApprovalEditItems ? (
                                                <td className="whitespace-nowrap px-3 py-2 text-right">
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        onClick={() => removeApprovalItem(item.id)}
                                                    >
                                                        Remover
                                                    </Button>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="space-y-4">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                {/* UM botão só: dois exportadores para a mesma
                                    OC era redundância que só gerava dúvida
                                    sobre qual arquivo mandar. Só aparece DEPOIS
                                    da aprovação — antes disso o conteúdo ainda
                                    muda, e arquivo que circula fora do sistema
                                    não volta atrás. */}
                                {isOrderApproved ? (
                                    <Button variant="secondary" onClick={downloadPurchaseOrderXlsx} disabled={saving}>
                                        Baixar pedido
                                    </Button>
                                ) : null}
                                {canCancelOrder ? (
                                    <Button variant="danger" onClick={cancelPurchaseOrder} disabled={saving}>
                                        Cancelar
                                    </Button>
                                ) : null}
                                {nextStatus ? (
                                    <Button onClick={advanceStatus} disabled={saving}>
                                        {nextStatus === 'OPEN'
                                            ? 'Aprovar e enviar ao fornecedor'
                                            : `Avancar para ${SUPPLIER_ACTION_LABELS[nextStatus] || STATUS_LABELS[nextStatus] || nextStatus}`}
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-mono-500 dark:text-mono-400">OC nao encontrada.</p>
                )}
            </Modal>
        </div>
    );
};

export default Purchasing;
